import React, { useContext, useEffect, useState, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoaderContext } from '../../../App';
import GHGOrgBoundaries from './GHGOrgBoundaries';
import { currentYear, startYear } from '../../../core/constants/common';
import { Breadcrumb, Button } from 'react-bootstrap';
import GHGOprBoundaries from './GHGOprBoundaries';
import IdentifyEmissionSource from './IdentifyEmissionSource';
import ReviewAndFinalize from './ReviewAndFinalize';
import { BiCheck, BiExit } from 'react-icons/bi';
import GHGEmissionService from '../../../core/service/GHGEmissionService';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';

const AddGHGInventory = ({ t }) => {
    // All hooks must be called at the top level, before any loops or conditional code
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setLoaderDisplay } = useContext(LoaderContext);
    
    const [passYear, setPassYear] = useState(searchParams.get('year') ?? null);
    const [passTable, setPassTable] = useState(searchParams.get('table') ?? null);
    const [showOrgBoundary, setShowOrgBoundary] = useState(true);
    const [showOprBoundary, setShowOprBoundary] = useState(false);
    const [showEmissionSource, setShowEmissionSource] = useState(false);
    const [showReview, setShowReview] = useState(false);

    // Unsaved data tracking
    const [hasUnsavedData, setHasUnsavedData] = useState(false);
    const [pendingPageChange, setPendingPageChange] = useState(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    
  
    const [datasets, setDatasets] = useState([]);
    const [finalized, setfinalized] = useState(false);
    const [orgData, setOrgData] = useState([]);
    const [hasAnswered, setHasAnswered] = useState(false);
    const [hasSelSources, setHasSelSources] = useState(false);

    const saveHandlerRef = useRef(null);

   

    useEffect(() => {
        getDataSet();
        getSummary();
    }, [])

    const year = new Date().getFullYear();
    const [filter, setFilter] = useState({
        year: passYear ?? `${year}`,
        emissionTable: passTable ?? 'GHG'
    });

    // Create yearOptions after all hooks are declared
    const yearOptions = [];
    for (let i = currentYear; i >= startYear; i--) {
        yearOptions.push(<option value={i} key={i}>{i}</option>);
    }

    const authorityData = JSON.parse(localStorage.getItem("authority"));
    const companyId = authorityData.profile.company.id;

    const registerSaveHandler = (handler) => {
        saveHandlerRef.current = handler;
    };

  

    const getDataSet = () => {
        // setLoaderDisplay(true);
        GHGEmissionService.getDataSet(companyId)
            .then((response) => {
                setLoaderDisplay(false);
                setDatasets(response.data.data);
            })
            .catch((error) => {
                setLoaderDisplay(false);
            });
    }

    const getSummary = () => {
        // setLoaderDisplay(true);
        const date = filter?.year + '-01-01';
        GHGEmissionService.getSummaryData(filter.emissionTable ?? 'GHG', companyId, date)
            .then((response) => {
                setLoaderDisplay(false);
                setfinalized(response.data.data['finalized']);
                setOrgData(response.data.data['organizationalBoundary']);
                setHasAnswered(hasAnsweredQuestions(response.data.data['operationalBoundary']));
                setHasSelSources(hasSelectedSources(response.data.data['emissionSource']))
            })
            .catch((error) => {
                setLoaderDisplay(false);
            });
    }

    const hasAnsweredQuestions = (data) => {
        for (const scope of data) {
            for (const category of scope.categories) {
                for (const question of category.questions) {
                    if (question.answer === "Yes" || question.answer === "No") {
                        return true;
                    }
                }
            }
        }
        return false;
    }

    const hasSelectedSources = (data) => {
        return Object.values(data).some(scope =>
            Object.values(scope).some(category =>
                category.some(source =>
                    source.alreadySelected === true ||
                    (source.factors && source.factors.some(factor => factor.alreadySelected === true))
                )
            ));
    };

    const handlePageChange = (page) => {
        if (hasUnsavedData) {
            setPendingPageChange(page);
            setShowConfirmationModal(true);
        } else {
            setPages(page);
        }
    };

    const handleConfirmationModal = async (confirmed) => {
        setShowConfirmationModal(false);
        if (confirmed) {
            if (typeof saveHandlerRef.current === 'function') {
                const saveSuccess = await saveHandlerRef.current(pendingPageChange);
                if (saveSuccess && pendingPageChange) {
                    setHasUnsavedData(false);
                    setPages(pendingPageChange);
                    setPendingPageChange(null);
                } else {
                    setHasUnsavedData(true);
                    setPendingPageChange(null);
                }
            }
        } else {
            setHasUnsavedData(false);
            if (pendingPageChange) {
                setPages(pendingPageChange);
                setPendingPageChange(null);
            }
        }
    };

    const setPages = (page) => {
        switch (page) {
            case 'Org':
                setShowOrgBoundary(true);
                setShowOprBoundary(false);
                setShowEmissionSource(false);
                setShowReview(false);
                break;
            case 'Opr':
                setShowOrgBoundary(false);
                setShowOprBoundary(true);
                setShowEmissionSource(false);
                setShowReview(false);
                break;
            case 'EmiSource':
                setShowOrgBoundary(false);
                setShowOprBoundary(false);
                setShowEmissionSource(true);
                setShowReview(false);
                break;
            case 'Review':
                setShowOrgBoundary(false);
                setShowOprBoundary(false);
                setShowEmissionSource(false);
                setShowReview(true);
                break;
            default:
                setShowOrgBoundary(true);
                setShowOprBoundary(false);
                setShowEmissionSource(false);
                setShowReview(false);
                break;
        }
    }

    const gotoInventory = () => {
        navigate(`/company_admin/ghg_inventory?year=` + filter.year + `&table=` + filter.emissionTable);
    }

    return (
        <>
            <div className="List_header mb-1">
                <div>
                    <Breadcrumb className='h5'>
                        <Breadcrumb.Item>{t("GHGEmissions.label")}</Breadcrumb.Item>
                        <Breadcrumb.Item active>{t("GHGInventory.label")}</Breadcrumb.Item>
                    </Breadcrumb>
                </div>
                <div className='GHGInventory_btn_head'>
                    <Button variant='primary' onClick={() => gotoInventory()}><BiExit /> {t("Exit.label")}</Button>
                    <select className="form-select  w-auto" value={filter.year} disabled={true}
                        onChange={(e) => {
                            setFilter({ ...filter, year: e.target.value });
                        }}>
                        {yearOptions}
                    </select>
                    {showEmissionSource &&
                        <select className="form-select  w-auto" value={filter.emissionTable}
                            onChange={async (e) => {
                                const newTable = e.target.value;
                                if (hasUnsavedData && typeof saveHandlerRef.current === 'function') {
                                    const saveSuccess = await saveHandlerRef.current(null);
                                    if (saveSuccess) {
                                        setFilter((prev) => ({ ...prev, emissionTable: newTable }));
                                    }
                                } else {
                                    setFilter((prev) => ({ ...prev, emissionTable: newTable }));
                                }
                            }}>
                            {datasets.map((table) => (
                                <option key={table.id} value={table.name}>
                                    {table.name}
                                </option>
                            ))}
                        </select>
                    }
                </div>
            </div>
            <div className={'DoubleMateriality_wizard_lineBox'}>
                <ul>
                    <li className={showOrgBoundary ? 'active select' : !showOrgBoundary && orgData.length > 0 ? 'done' : 'cursor'}
                        onClick={() => handlePageChange('Org')}>
                        {showOrgBoundary && orgData.length == 0 && <span className={''}>1</span>}
                        {showOrgBoundary && orgData.length > 0 && <span className={''}>1</span>}
                        {!showOrgBoundary && orgData.length == 0 && <span className={''}>1</span>}
                        {!showOrgBoundary && orgData.length > 0 && <span className={''}><BiCheck style={{ fontSize: '18px' }} /></span>}
                        {t("DefineOrgBoundary.label")}
                    </li>
                    <li className={showOprBoundary ? 'active select' : !showOprBoundary && hasAnswered ? 'done' : 'cursor'}
                        onClick={() => handlePageChange('Opr')}>
                        {showOprBoundary && !hasAnswered && <span className={''}>2</span>}
                        {showOprBoundary && hasAnswered && <span className={''}>2</span>}
                        {!showOprBoundary && !hasAnswered && <span className={''}>2</span>}
                        {!showOprBoundary && hasAnswered && <span className={''}><BiCheck style={{ fontSize: '18px' }} /></span>}
                        {t("DefineOprBoundary.label")}
                    </li>
                    <li className={showEmissionSource ? 'active select' : !showEmissionSource && hasSelSources ? 'done' : 'cursor'}
                        onClick={() => handlePageChange('EmiSource')}>
                        {showEmissionSource && !hasSelSources && <span className={''}>3</span>}
                        {showEmissionSource && hasSelSources && <span className={''}>3</span>}
                        {!showEmissionSource && !hasSelSources && <span className={''}>3</span>}
                        {!showEmissionSource && hasSelSources && <span className={''}><BiCheck style={{ fontSize: '18px' }} /></span>}
                        {t("IdentifyEmission.label")}
                    </li>
                    <li className={showReview ? 'active select' : 'cursor'}
                        onClick={() => handlePageChange('Review')}>
                        <span className={''}>4</span>
                        {t("ReviewAndFinalize.label")}
                    </li>
                </ul>
            </div>
            <div>
                {
                    showOrgBoundary && <GHGOrgBoundaries
                        filterData={filter}
                        nextPage={(e) => { setPages(e) }}
                        setHasUnsavedData={setHasUnsavedData}
                        gotoPage={(e) => { setPages(e) }}
                        registerSaveHandler={registerSaveHandler}
                    />
                }
                {
                    showOprBoundary && <GHGOprBoundaries
                        filterData={filter}
                        nextPage={(e) => { setPages(e) }}
                        prevPage={(e) => { setPages(e) }}
                        gotoPage={(e) => { setPages(e) }}
                        setHasUnsavedData={setHasUnsavedData}
                        registerSaveHandler={registerSaveHandler}
                    />
                }
                {
                    showEmissionSource && <IdentifyEmissionSource
                        filterData={filter}
                        nextPage={(e) => { setPages(e) }}
                        prevPage={(e) => { setPages(e) }}
                        gotoPage={(e) => { setPages(e) }}
                        setHasUnsavedData={setHasUnsavedData}
                        registerSaveHandler={registerSaveHandler}
                    />
                }
                {
                    showReview && <ReviewAndFinalize
                        filterData={filter}
                        prevPage={(e) => { setPages(e) }}
                        noShowFooter={false}
                        setHasUnsavedData={setHasUnsavedData}
                    />
                }
            </div>

            {showConfirmationModal &&
                <ConfirmationModal
                    modalShow={true}
                    // title={t("UnsavedChanges2.label")}
                    messageText={t("UnsavedChangesMessage.label")}
                    confirmBtn={t("Save.label")}
                    CancelBtn={t("Cancel.label")}
                    callbackModal={handleConfirmationModal}
                />}

        </>
    );
}

export default withTranslation()(AddGHGInventory);